import { type IWorkfrontValueProposal, WorkfrontValueMatch } from '@atlas/types'
import { Button, Checkbox, DialogContent, DialogTitle, Spinner } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'
import { MatchBadge } from './MappingBadges'

export interface ProposalsDialogProps {
	open: boolean
	axisLabel: string
	/** Null while the proposals load. */
	proposals: IWorkfrontValueProposal[] | null
	submitting?: boolean
	onAccept: (accepted: IWorkfrontValueProposal[]) => void
	onCancel: () => void
}

const keyOf = (proposal: IWorkfrontValueProposal) => `${proposal.label}→${proposal.valueId}`

/**
 * Review before accepting: exact matches start checked, close ones unchecked, so a
 * close match is only accepted by someone who looked at it.
 */
export function ProposalsDialog({ open, axisLabel, proposals, submitting, onAccept, onCancel }: ProposalsDialogProps) {
	const [checked, setChecked] = useState<Set<string>>(new Set())
	useEffect(() => {
		if (open && proposals) {
			setChecked(new Set(proposals.filter((p) => p.match === WorkfrontValueMatch.EXACT).map(keyOf)))
		}
	}, [open, proposals])
	if (!open) return null

	const toggle = (key: string) => {
		const next = new Set(checked)
		if (next.has(key)) next.delete(key)
		else next.add(key)
		setChecked(next)
	}
	const accepted = (proposals ?? []).filter((proposal) => checked.has(keyOf(proposal)))

	return (
		<DialogContent size="lg" isOpen={open} onOpenChange={(next) => (next ? undefined : onCancel())}>
			<DialogTitle>Proposals for {axisLabel}</DialogTitle>
			<div className="mt-4">
				{!proposals ? (
					<Spinner aria-label="Loading the proposals" />
				) : proposals.length === 0 ? (
					<p className="text-ds-sm text-ds-text-muted">No exact or close match for the unmatched labels.</p>
				) : (
					<ul aria-label="Proposals" className="space-y-2">
						{proposals.map((proposal) => (
							<li key={keyOf(proposal)} className="flex items-center gap-2 text-ds-sm">
								<Checkbox isSelected={checked.has(keyOf(proposal))} onChange={() => toggle(keyOf(proposal))}>
									{proposal.label} → {proposal.valueLabel}
								</Checkbox>
								<MatchBadge match={proposal.match} />
							</li>
						))}
					</ul>
				)}
			</div>
			<div className="mt-6 flex justify-end gap-2">
				<Button variant="outline" onPress={onCancel}>
					Cancel
				</Button>
				<Button isDisabled={accepted.length === 0 || submitting} onPress={() => onAccept(accepted)}>
					{submitting ? 'Accepting…' : `Accept ${accepted.length}`}
				</Button>
			</div>
		</DialogContent>
	)
}
